import { Button, Card, Divider, Grid, Stack, Typography } from '@mui/material';
import clsx from 'clsx';
import { find, map } from 'lodash';
import { ComponentType } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { useDialog } from '../../../hooks';
import { ConfirmDialog } from '../../dialogs';
import useStyles from './styles';

interface TestQuestionsNavProps {
  questions: Record<string, any>[];
  scrollTo?: (index: number) => void;
  selected: Record<string, number>[];
  total: number;
  done: number;
  mode?: 'test' | 'result';
  onFinish?: () => void;
  isPassed?: boolean;
}
export const TestQuestionsNav: ComponentType<TestQuestionsNavProps> = ({
  questions = [],
  selected = [],
  scrollTo,
  total,
  done,
  mode = 'test',
  onFinish,
  isPassed,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openConfirm, closeConfirm] = useDialog(ConfirmDialog);

  const title =
    mode === 'result'
      ? t('test.result', 'Результаты теста')
      : t('test.navigation', 'Навигация по вопросам');
  const doneText =
    mode === 'result' ? t('test.correct', 'Правильных ответов') : t('test.answered', 'Отвечено');
  const negativeText =
    mode === 'result'
      ? t('test.incorrect', 'Неправильных ответов')
      : t('test.notAnswered', 'Не отвечено');

  const handleClick = (index: number) => {
    if (scrollTo) scrollTo(index);
  };

  const handleFinishExam = () => {
    openConfirm({
      size: 'xs',
      resolve: () => {
        if (onFinish) onFinish();
        closeConfirm();
      },
    });
  };

  const handleColor = (id: number | string) => {
    const item = find(selected, (x) => x.question === id);
    if (mode === 'result') {
      return item && item.isCorrect ? 'success' : 'error';
    }
    return item ? 'primary' : 'warning';
  };

  return (
    <Card className={classes.root} component={Stack}>
      <Typography variant="subtitle2" mb={2.5}>
        {title}
      </Typography>
      <Grid
        container
        className={clsx(classes.hints, { [classes.hintsResult]: mode === 'result' })}
        direction="row"
        spacing={2}
        justifyContent="space-between"
        mb={3}
      >
        <Grid item>
          <Typography
            variant="button_sm"
            component="div"
            className={clsx(classes.hint, classes.done)}
          >
            {doneText}
            <Typography variant="subtitle3">{done}</Typography>
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            variant="button_sm"
            component="div"
            className={clsx(classes.hint, classes.left)}
          >
            {negativeText}
            <Typography variant="subtitle3">{total - done}</Typography>
          </Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        {map(questions, (item, number: number) => (
          <Grid item key={number}>
            <Button
              color={handleColor(item.id)}
              variant="contained"
              size="small"
              className={classes.questionBtn}
              onClick={() => handleClick(number)}
            >
              {item.droppedQuestionId}
            </Button>
          </Grid>
        ))}
      </Grid>
      <Divider sx={{ mt: 4, mb: 4 }} />
      {mode === 'result' ? (
        <>
          {!isPassed && (
            <Typography
              component={Link}
              to={ROUTES.APPEALS.ROOT}
              variant="body2"
              mb={4}
              className={classes.appeal}
            >
              <Trans i18nKey="appeal.link.here">
                Подать на апелляцию вы можете <i>здесь</i>
              </Trans>
            </Typography>
          )}
          <Button
            component={Link}
            to={ROUTES.CABINET}
            variant="contained"
            color="secondary"
            size="large"
            fullWidth
          >
            {t('cabinet.home', 'В личный кабинет')}
          </Button>
        </>
      ) : (
        <Button onClick={handleFinishExam} variant="contained" color="secondary" fullWidth>
          {t('test.complete', 'Завершить тест')}
        </Button>
      )}
    </Card>
  );
};
